/**
 * Plan ordering helpers.
 *
 * Plans are ranked by monthly price. The backend decides upgrade-vs-downgrade
 * from Stripe's own unit_amount rather than from these numbers, so this is only
 * ever used to choose what to *offer* - never to predict what will be charged.
 */

const FREE_PRICE = 0;

/**
 * Monthly price of a plan as a number, whatever shape the API sent.
 *
 * @param {Object|null} plan
 * @returns {number} the price, or 0 when it is missing or unparseable
 */
export function monthlyPriceOf(plan) {
  const price = parseFloat(plan?.price_monthly);
  return Number.isFinite(price) ? price : FREE_PRICE;
}

/**
 * The paid plans that cost more than the current one, cheapest first.
 *
 * Used by the limit-reached dialog: someone who has run out of room needs a
 * bigger plan, so showing them a cheaper one would only be a way to make the
 * problem worse.
 *
 * @param {Array<Object>} plans - all available plans
 * @param {Object|null} currentPlan - the plan the tenant is on
 * @returns {Array<Object>} upgrade options, ascending by monthly price
 */
export function upgradeOptionsFor(plans, currentPlan) {
  if (!Array.isArray(plans)) return [];
  const currentPrice = monthlyPriceOf(currentPlan);

  return plans
    .filter((plan) => plan?.id !== currentPlan?.id)
    .filter((plan) => monthlyPriceOf(plan) > currentPrice)
    .sort((a, b) => monthlyPriceOf(a) - monthlyPriceOf(b));
}
